import React, { VFC, useCallback, useEffect, useRef, useState } from 'react';

import FileDropzone from 'src/common/FileDropzone';
import { Nullable } from 'src/types/nullable.type';
import { useStepEditorContext } from 'src/store/step-editor';
import { EventBus } from 'src/common/EventBus';
import { useHtmlArtifactUploader } from '../../use-html-artifact-uploader.hook';
import { HtmlArtifactFrame } from 'src/common/HtmlArtifactFrame';
import { StepEditorContentConfigureStageTypeEnum } from '../configure/stage-type.enum';

/**
 * The step editor's HTML entry point (JOU-40).
 *
 * Upload and paste sit on one screen rather than behind a tab. Someone who has
 * just built something in a chat window has HTML in their clipboard, not a file
 * on disk, so making paste secondary would hide the case most people arrive
 * with — the same reasoning as the block editor's panel.
 */
export const StepEditorContentHtmlUpload: VFC<{}> = () => {
  const inputRef = useRef<Nullable<HTMLElement>>(null);
  const [source, setSource] = useState('');
  const uploadedS3Asset = useStepEditorContext((state) => state.uploadedS3Asset);
  const uploadInProgress = useStepEditorContext((state) => state.uploadInProgress);
  const uploadPercentage = useStepEditorContext((state) => state.uploadPercentage);
  const currentConfigurationStage = useStepEditorContext((state) => state.currentConfigurationStage);
  const { uploadHtmlFile, submitHtmlSource, error, setError } = useHtmlArtifactUploader();

  // Clear the uploader when this panel goes away, so the next step does not
  // open holding the previous artifact.
  //
  // Read straight off the store rather than through a hook dependency. An
  // earlier version listed resetUploader in the dependency array while also
  // destructuring the whole store, so the component re-rendered on every store
  // write — including this panel's own upload — and the cleanup ran mid-edit,
  // wiping the artifact that had just been attached. Empty deps means this
  // runs on unmount and only on unmount.
  useEffect(() => {
    return () => {
      useStepEditorContext.getState().resetUploader();
    };
  }, []);

  const onFilesUpdate = useCallback(
    (files: File[]) => {
      if (!files || files.length === 0) {
        return;
      }
      const [file] = files;
      // The picker is scoped to .html, but a drag-and-drop bypasses that
      // filter, so the check has to happen here too. The server sniffs the
      // bytes regardless — this is only for a fast, specific message.
      const looksHtml = file.type === 'text/html' || /\.html?$/i.test(file.name);
      if (!looksHtml) {
        setError('That is not an HTML file.');
        return;
      }
      // Move to the configure stage before the upload starts, exactly as the
      // file panel does. That stage is what renders StepEditorContentConfigurePanel
      // — the form carrying the Save button — so without it the artifact
      // uploads and previews but there is nothing to attach it to the step,
      // and the step is created with no nodes.
      useStepEditorContext.getState().setCurrentConfigurationStage(
        StepEditorContentConfigureStageTypeEnum.CONFIGURE
      );
      uploadHtmlFile(file);
    },
    [uploadHtmlFile, setError]
  );

  const onUploadClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const onPasteSubmit = useCallback(() => {
    // Same reason as the upload path: the configure stage carries the Save
    // button, so pasted HTML needs it too.
    useStepEditorContext.getState().setCurrentConfigurationStage(
      StepEditorContentConfigureStageTypeEnum.CONFIGURE
    );
    submitHtmlSource(source);
  }, [submitHtmlSource, source]);

  if (uploadedS3Asset?.url) {
    const onEditClicked = () => {
      EventBus.emit('editor:reset-content');
    };

    return (
      <>
        {currentConfigurationStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE && (
          <div className='flex flex-col space-y-4'>
            <div className='flex items-center justify-between'>
              <span className='text-bedrock-p text-bedrock-black'>{uploadedS3Asset.name}</span>
              <button type='button' className='text-bedrock-p text-bedrock-link' onClick={onEditClicked}>
                Replace
              </button>
            </div>
            <HtmlArtifactFrame
              url={uploadedS3Asset.url}
              title={uploadedS3Asset.name}
              // A preview, not a place to use the artifact: clicks here belong
              // to the editor around it.
              interactionDisabled
              // The left panel is a fixed-height column, so the artifact
              // scrolls inside its own frame rather than stretching the panel.
              fillParent
              className='h-[420px]'
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className='flex flex-col space-y-6'>
      <div className='text-bedrock-p text-bedrock-black'>
        Add an interactive HTML page, such as something built in Claude or ChatGPT. It runs inside this
        Step, isolated from the rest of your Journey.
      </div>

      <button
        type='button'
        className='btn-bedrock-primary'
        onClick={onUploadClick}
        disabled={uploadInProgress}
      >
        {uploadInProgress ? `Uploading ${uploadPercentage}%` : 'Upload an HTML file'}
      </button>

      <div className='hidden'>
        <FileDropzone
          type='file'
          className='file-dropzone'
          multiple={false}
          accept='text/html,.html,.htm'
          onFilesUpdate={onFilesUpdate}
          resetOnSuccess={true}
          dropzoneClasses={null}
          inputRef={inputRef}
        />
      </div>

      <div className='flex items-center gap-2'>
        <span className='h-px flex-1 bg-bedrock-light-gray' />
        <span className='text-bedrock-p text-bedrock-dark-gray'>or paste it</span>
        <span className='h-px flex-1 bg-bedrock-light-gray' />
      </div>

      <textarea
        className='input-bedrock w-full h-40 font-mono text-bedrock-serif-small'
        placeholder='Paste your HTML here'
        value={source}
        onChange={(e) => setSource(e.target.value)}
        disabled={uploadInProgress}
      />

      <button
        type='button'
        className='btn-bedrock-secondary'
        onClick={onPasteSubmit}
        disabled={uploadInProgress || !source.trim()}
      >
        Add pasted HTML
      </button>

      {error && <p className='text-bedrock-p text-bedrock-red'>{error}</p>}

      {/* See the matching note in the block editor's panel: we modify the
          uploaded file, so we say so. */}
      <p className='text-bedrock-serif-small text-bedrock-dark-gray'>
        Journey adds a small script and style to your file so it sizes and scrolls smoothly inside the Journey.
      </p>
    </div>
  );
};

export default StepEditorContentHtmlUpload;
